"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { register, getRegisterConfig, type RegistrationMode } from "@/lib/auth";
import { useUserStore } from "@/lib/store/user";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function RegisterPage() {
  const { setUser } = useUserStore();
  const [mode, setMode] = useState<RegistrationMode | null>(null);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [wechatId, setWechatId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 邀请链接形如 /register?code=XXXX，自动填入邀请码
    const code = new URLSearchParams(window.location.search).get("code");
    if (code) setInviteCode(code.toUpperCase());

    getRegisterConfig()
      .then((cfg) => setMode(cfg.mode))
      .catch(() => setMode("invite"));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("两次输入的密码不一致");
      return;
    }

    setLoading(true);

    try {
      const res = await register({
        email,
        username,
        password,
        ...(mode === "invite"
          ? { invite_code: inviteCode.trim(), wechat_id: wechatId.trim() }
          : {}),
      });
      setUser(res.user);
      window.location.href = "/workspace";
    } catch (err) {
      setError(err instanceof Error ? err.message : "注册失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="创建账户" subtitle="注册智写，开启 AI 辅助写作之旅">
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div
            role="alert"
            className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <div className="space-y-4">
          {mode === "invite" && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="inviteCode" className="text-sm font-medium">
                  邀请码
                </Label>
                <Input
                  id="inviteCode"
                  placeholder="输入老师发给你的邀请码"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  className="h-11 rounded-xl border-border/80 bg-background font-mono shadow-sm"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wechatId" className="text-sm font-medium">
                  微信号
                </Label>
                <Input
                  id="wechatId"
                  placeholder="填写你的微信号，方便老师核对身份"
                  value={wechatId}
                  onChange={(e) => setWechatId(e.target.value)}
                  className="h-11 rounded-xl border-border/80 bg-background shadow-sm"
                  required
                />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="username" className="text-sm font-medium">
              用户名
            </Label>
            <Input
              id="username"
              placeholder="输入用户名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-11 rounded-xl border-border/80 bg-background shadow-sm"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-sm font-medium">
              邮箱
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 rounded-xl border-border/80 bg-background shadow-sm"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-sm font-medium">
              密码
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="至少 6 位"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 rounded-xl border-border/80 bg-background shadow-sm"
              required
              minLength={6}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword" className="text-sm font-medium">
              确认密码
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="再次输入密码"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-11 rounded-xl border-border/80 bg-background shadow-sm"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          className="h-11 w-full rounded-xl text-sm font-medium"
          disabled={loading || mode === null}
        >
          {loading ? "注册中..." : "创建账户"}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          已有账户？{" "}
          <Link
            href="/login"
            className="font-medium text-foreground underline-offset-4 transition-colors hover:underline"
          >
            登录
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
